import { useState, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { KeenIcon } from "@/components";
import { Modal } from "@/components/ui/modal";
import { Input, Table, Popconfirm, Tooltip } from "antd";
import { Button as AntdButton } from "antd";
import {
  faMagnifyingGlass,
  faPaperclip,
  faPenToSquare,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useDeleteAttachMutation,
  useSearchAttachFile,
  useUploadAttach,
  useUpdateAttach,
} from "../../../../../hooks/useAgentAttachFile";
import { Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { TablePagination } from "../../../../../components/pagination/TablePagination";
import PropTypes from "prop-types";
import { useQueryClient } from "@tanstack/react-query";

const ManageAttach = ({ tableProps = {}, className = "" }) => {
  const queryClient = useQueryClient();
  const intl = useIntl();
  // ===== STATE MANAGEMENT =====
  const [open, setOpen] = useState(false);
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Form state
  const [attachFile, setAttachFile] = useState(null);
  const [attachName, setAttachName] = useState("");
  const [attachDetail, setAttachDetail] = useState("");
  const [attachValidationErrors, setAttachValidationErrors] = useState({});

  // Edit form state
  const [editFile, setEditFile] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDetail, setEditDetail] = useState("");
  const [editingRecord, setEditingRecord] = useState(null);
  const [editValidationErrors, setEditValidationErrors] = useState({});

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);

  // Search state
  const [searchParams, setSearchParams] = useState({
    page: 1,
    limit: 5,
    name: "",
  });
  const [searchDraft, setSearchDraft] = useState({
    page: 1,
    limit: 5,
    name: "",
  });

  // Local data state
  const [localAttachData, setLocalAttachData] = useState({
    result: [],
    total: 0,
  });

  // ===== API HOOKS =====
  const { mutateAsync: uploadFile, isPending } = useUploadAttach();
  const { mutateAsync: updateFile, isLoading: updateLoading } =
    useUpdateAttach();
  const { mutateAsync: deleteAttach, isLoading: deleteLoading } =
    useDeleteAttachMutation();
  const { data: attachResponseSearch, isLoading: searchLoading } =
    useSearchAttachFile(searchParams);

  // ===== EFFECTS =====
  useEffect(() => {
    if (attachResponseSearch) {
      setLocalAttachData(attachResponseSearch);
    }
  }, [attachResponseSearch]);

  // Đồng bộ page state với searchParams.page
  useEffect(() => {
    setPage(searchParams.page);
  }, [searchParams.page]);

  useEffect(() => {
    // Chỉ chạy khi dữ liệu đã được load từ API (không phải giá trị khởi tạo)
    // và khi result.length === 0 và page > 1 (tránh giảm page xuống dưới 1)
    if (
      !searchLoading &&
      localAttachData?.result?.length === 0 &&
      searchParams.page > 1
    ) {
      setSearchParams((prev) => ({
        ...prev,
        page: 1,
      }));
      const invalidateQueries = setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ["attach-search", searchParams?.limit, searchParams?.page, searchParams?.name],
        });
      }, 2000);
      setPage(searchParams?.page);
      return () => clearTimeout(invalidateQueries);
    }
  }, [localAttachData?.result?.length, searchLoading, searchParams.page]);

  // ===== EVENT HANDLERS =====
  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSearchParams((prev) => ({
      ...prev,
      ...searchDraft,
      page: newPage,
      limit: limit,
    }));
  };

  const handlePageSizeChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
    setSearchParams((prev) => ({
      ...prev,
      ...searchDraft,
      page: 1,
      limit: newLimit,
    }));
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearchDraft({ ...searchDraft, name: value });
  };

  const handleSearch = () => {
    setSearchParams({ ...searchParams, ...searchDraft, page: 1 });
    setPage(1);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setEditName(record.name || "");
    setEditDetail(record.detail || "");
    setEditFile(null); // Reset file selection
    setShowEditModal(true);
    setEditValidationErrors({});
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingRecord) return;

    // Validate thủ công
    setEditValidationErrors({});
    const errors = {};
    if (!editName.trim()) {
      errors.name = "Tên không được để trống";
    }
    if (!editDetail.trim()) {
      errors.detail = "Chi tiết không được để trống";
    }
    if (Object.keys(errors).length > 0) {
      setEditValidationErrors(errors);
      return;
    }

    try {
      const resp = await updateFile({
        id: editingRecord.id,
        data: {
          name: editName,
          detail: editDetail,
          ...(editFile && { file: editFile }), // Only include file if a new one is selected
        },
      });

      setLocalAttachData((prev) => ({
        ...prev,
        result: prev.result.map((item) =>
          item.id === editingRecord.id
            ? {
                ...item,
                name: resp?.name ?? editName,
                detail: resp?.detail ?? editDetail,
                filename: resp?.filename ?? item.filename,
                updated_at: resp?.updated_at ?? Date.now(),
                ...(resp?.source && { source: resp.source }),
              }
            : item
        ),
      }));
    } catch (error) {
      console.error("Update error:", error);
    } finally {
      setShowEditModal(false);
      setEditingRecord(null);
      setEditFile(null);
      setEditName("");
      setEditDetail("");
      setEditValidationErrors({});
    }
  };

  const handleDelete = async (record) => {
    try {
      await deleteAttach(record.id);
      setLocalAttachData((prev) => ({
        ...prev,
        result: prev.result.filter((resultItem) => resultItem.id !== record.id),
        total: (prev.total || 1) - 1,
      }));
    } catch (error) {
      console.error("Lỗi khi xóa:", error);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!attachFile) return;

    // Validate thủ công
    setAttachValidationErrors({});
    const errors = {};
    if (!attachName.trim()) {
      errors.name = "Tên không được để trống";
    }
    if (!attachDetail.trim()) {
      errors.detail = "Chi tiết không được để trống";
    }
    if (Object.keys(errors).length > 0) {
      setAttachValidationErrors(errors);
      return;
    }

    try {
      const resp = await uploadFile({
        file: attachFile,
        name: attachName,
        detail: attachDetail,
      });
      const data = resp?.data;

      setLocalAttachData((prev) => {
        const newTotal = (prev.total || 0) + 1;
        const currentPageSize = prev.result.length;

        // Nếu page hiện tại đã đầy, chuyển về page cuối để hiển thị file mới
        if (currentPageSize >= limit) {
          const lastPage = Math.ceil(newTotal / limit);

          setSearchParams((prevParams) => ({
            ...prevParams,
            page: lastPage,
          }));
          const invalidateQueries = setTimeout(() => {
            queryClient.invalidateQueries({
              queryKey: [
                "attach-search",
                searchParams?.limit,
                searchParams?.page,
                searchParams?.name,
              ],
            });
          }, 2000);
          return () => clearTimeout(invalidateQueries);
        }

        return {
          ...prev,
          result: [
            {
              id: data?.id,
              name: data?.name ?? attachName,
              filename: data?.filename,
              created_at: data?.created_at,
              updated_at: data?.updated_at,
              source: data?.source,
              detail: data?.detail ?? attachDetail,
            },
            ...prev.result,
          ],
          total: newTotal,
        };
      });

      // Close modals and reset form
      setShowAttachModal(false);
      setAttachFile(null);
      setAttachName("");
      setAttachDetail("");
      setAttachValidationErrors({});
    } catch (error) {
      console.error("Upload error:", error);
    }
  };

  // ===== TABLE COLUMNS =====
  const columns = [
    {
      key: "name",
      title: <FormattedMessage id="ATTACH_FILES.NAME" defaultMessage="Name" />,
      dataIndex: "name",
      width: "250px",
    },
    {
      key: "filename",
      title: (
        <FormattedMessage
          id="ATTACH_FILES.FILENAME"
          defaultMessage="File Name"
        />
      ),
      dataIndex: "filename",
      width: "250px",
    },
    {
      key: "created_at",
      title: (
        <FormattedMessage
          id="ATTACH_FILES.CREATED_AT"
          defaultMessage="Create at"
        />
      ),
      dataIndex: "created_at",
      width: "150px",
      render: (text) => (
        <p>{dayjs(parseInt(text)).format("DD-MM-YYYY HH:mm:ss")}</p>
      ),
      sorter: (a, b) => a.created_at - b.created_at,
    },
    {
      key: "updated_at",
      title: (
        <FormattedMessage
          id="ATTACH_FILES.UPDATED_AT"
          defaultMessage="Update at"
        />
      ),
      dataIndex: "updated_at",
      width: "150px",
      render: (text) => (
        <p>{dayjs(parseInt(text)).format("DD-MM-YYYY HH:mm:ss")}</p>
      ),
      sorter: (a, b) => a.updated_at - b.updated_at,
    },
    {
      key: "detail",
      title: (
        <FormattedMessage id="ATTACH_FILES.DETAIL" defaultMessage="Detail" />
      ),
      dataIndex: "detail",
      width: "200px",
    },
    {
      key: "actions",
      title: (
        <FormattedMessage id="ATTACH_FILES.ACTIONS" defaultMessage="Actions" />
      ),
      width: 100,
      className: "text-center",
      align: "center",
      render: (_value, record) => (
        <div className="flex gap-4 justify-center">
          <button
            onClick={() => handleEdit(record)}
            className="text-blue-500 hover:text-blue-300"
          >
            <FontAwesomeIcon icon={faPenToSquare} />
          </button>
          <Popconfirm
            title={intl.formatMessage({
              id: "ATTACH_FILES.DELETE_TITLE",
              defaultMessage: "Delete the file",
            })}
            description={intl.formatMessage({
              id: "ATTACH_FILES.DELETE_DESCRIPTION",
              defaultMessage: "Are you sure to delete this file?",
            })}
            onConfirm={() => handleDelete(record)}
            okText={intl.formatMessage({
              id: "ATTACH_FILES.YES",
              defaultMessage: "Yes",
            })}
            cancelText={intl.formatMessage({
              id: "ATTACH_FILES.NO",
              defaultMessage: "No",
            })}
          >
            <button
              className="text-red-500 hover:text-red-300"
              disabled={deleteLoading}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  // ===== RENDER FUNCTIONS =====
  const renderEditModal = () => (
    <Modal
      open={showEditModal}
      onClose={() => setShowEditModal(false)}
      title={<FormattedMessage id="ATTACH_FILES.EDIT_FILE" defaultMessage="Edit File" />}
      footer={null}
      width="700px"
      centered
    >
      <form className="flex flex-col gap-4" onSubmit={handleUpdate}>
        <label className="text-[#626262] text-base font-normal">
          <FormattedMessage id="ATTACH_FILES.FILE" defaultMessage="File" />
        </label>
        <div className="flex items-center gap-4">
          <Upload
            beforeUpload={(file) => {
              // Validate file type
              const allowedTypes = [
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "text/plain",
              ];
              if (!allowedTypes.includes(file.type)) {
                Modal.error({
                  title: intl.formatMessage({
                    id: "ATTACH_FILES.INVALID_FILE_TYPE",
                    defaultMessage: "Loại file không hợp lệ",
                  }),
                  content: intl.formatMessage({
                    id: "ATTACH_FILES.INVALID_FILE_TYPE_MESSAGE",
                    defaultMessage:
                      "File phải có định dạng pdf, doc, docx hoặc txt.",
                  }),
                  okText: intl.formatMessage({
                    id: "ATTACH_FILES.OK",
                    defaultMessage: "OK",
                  }),
                });
                return false;
              }

              // Validate file size (50MB)
              const maxSize = 50 * 1024 * 1024; // 50MB in bytes
              if (file.size > maxSize) {
                Modal.error({
                  title: intl.formatMessage({
                    id: "ATTACH_FILES.FILE_TOO_LARGE",
                    defaultMessage: "File quá lớn",
                  }),
                  content: intl.formatMessage({
                    id: "ATTACH_FILES.FILE_TOO_LARGE_MESSAGE",
                    defaultMessage: "File không được vượt quá 50MB.",
                  }),
                  okText: intl.formatMessage({
                    id: "ATTACH_FILES.OK",
                    defaultMessage: "OK",
                  }),
                });
                return false;
              }

              setEditFile(file);
              return false;
            }}
            showUploadList={false}
            maxCount={1}
            accept=".pdf,.doc,.docx,.txt"
          >
            <button
              type="button"
              className="px-2 py-2 text-sm bg-[#50B553] text-white rounded hover:bg-[#4AA642]"
            >
              <UploadOutlined />{" "}
              <FormattedMessage
                id="ATTACH_FILES.SELECT_NEW_FILE"
                defaultMessage="Chọn tệp mới"
              />
            </button>
          </Upload>

          <div className="flex items-center gap-2 text-xs text-gray-700 max-w-xs">
            <span className="truncate">
              {editFile
                ? editFile.name
                : editingRecord?.filename ||
                  intl.formatMessage({
                    id: "ATTACH_FILES.NO_FILE",
                    defaultMessage: "Không có file",
                  })}
            </span>
            {editFile && (
              <button
                onClick={() => setEditFile(null)}
                className="text-red-500 hover:text-red-700 font-semibold"
                type="button"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            )}
          </div>
        </div>

        <label className="text-[#626262] text-base font-normal">
          <span className="inline-flex items-center gap-1">
            <span className="text-red-500">*</span>
            <FormattedMessage id="ATTACH_FILES.NAME" defaultMessage="Name" />
          </span>
        </label>
        <Tooltip
          title={editValidationErrors.name}
          open={!!editValidationErrors.name}
          placement="topLeft"
          color="#ff4d4f"
        >
          <input
            type="text"
            className={`w-full border rounded px-3 py-2 bg-white text-sm hover:border-[#50B553] focus:border-[#50B553] outline-none ${
              editValidationErrors.name ? "border-red-500" : ""
            }`}
            value={editName}
            onChange={(e) => {
              setEditName(e.target.value);
              if (editValidationErrors.name) {
                setEditValidationErrors((prev) => {
                  const next = { ...prev };
                  delete next.name;
                  return next;
                });
              }
            }}
          />
        </Tooltip>

        <label className="text-[#626262] text-base font-normal">
          <span className="inline-flex items-center gap-1">
            <span className="text-red-500">*</span>
            <FormattedMessage id="ATTACH_FILES.DETAIL" defaultMessage="Detail" />
          </span>
        </label>
        <Tooltip
          title={editValidationErrors.detail}
          open={!!editValidationErrors.detail}
          placement="topLeft"
          color="#ff4d4f"
        >
          <textarea
            className={`w-full border rounded px-3 py-2 bg-white text-sm hover:border-[#50B553] focus:border-[#50B553] outline-none ${
              editValidationErrors.detail ? "border-red-500" : ""
            }`}
            value={editDetail}
            onChange={(e) => {
              setEditDetail(e.target.value);
              if (editValidationErrors.detail) {
                setEditValidationErrors((prev) => {
                  const next = { ...prev };
                  delete next.detail;
                  return next;
                });
              }
            }}
            rows={4}
          />
        </Tooltip>

        <div className="flex justify-end gap-2 mt-2">
          <AntdButton onClick={() => setShowEditModal(false)} disabled={updateLoading}>
            <FormattedMessage id="ATTACH_FILES.CANCEL" defaultMessage="Cancel" />
          </AntdButton>
          <AntdButton type="primary" htmlType="submit" loading={updateLoading}>
            <FormattedMessage id="ATTACH_FILES.UPDATE" defaultMessage="Update" />
          </AntdButton>
        </div>
      </form>
    </Modal>
  );

  const renderAttachModal = () => (
    <Modal
      open={showAttachModal}
      onClose={() => {
        setShowAttachModal(false);
        setAttachValidationErrors({});
      }}
      title={<FormattedMessage id="ATTACH_FILES.ATTACH_FILE_BUTTON" defaultMessage="Attach File" />}
      footer={null}
      width="500px"
      centered
    >
      <form className="flex flex-col gap-4" onSubmit={handleUpload}>
        <label className="text-[#626262] text-base font-normal">
          <span className="inline-flex items-center gap-1">
            <span className="text-red-500">*</span>
            <FormattedMessage id="ATTACH_FILES.FILE" defaultMessage="File" />
          </span>
        </label>
        <div className="flex items-center gap-4">
          <Upload
            beforeUpload={(file) => {
              // Validate file type
              const allowedTypes = [
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "text/plain",
              ];
              if (!allowedTypes.includes(file.type)) {
                Modal.error({
                  title: intl.formatMessage({
                    id: "ATTACH_FILES.INVALID_FILE_TYPE",
                    defaultMessage: "Loại file không hợp lệ",
                  }),
                  content: intl.formatMessage({
                    id: "ATTACH_FILES.INVALID_FILE_TYPE_MESSAGE",
                    defaultMessage:
                      "File phải có định dạng pdf, doc, docx hoặc txt.",
                  }),
                  okText: intl.formatMessage({
                    id: "ATTACH_FILES.OK",
                    defaultMessage: "OK",
                  }),
                });
                return false;
              }

              // Validate file size (50MB)
              const maxSize = 50 * 1024 * 1024; // 50MB in bytes
              if (file.size > maxSize) {
                Modal.error({
                  title: intl.formatMessage({
                    id: "ATTACH_FILES.FILE_TOO_LARGE",
                    defaultMessage: "File quá lớn",
                  }),
                  content: intl.formatMessage({
                    id: "ATTACH_FILES.FILE_TOO_LARGE_MESSAGE",
                    defaultMessage: "File không được vượt quá 50MB.",
                  }),
                  okText: intl.formatMessage({
                    id: "ATTACH_FILES.OK",
                    defaultMessage: "OK",
                  }),
                });
                return false;
              }

              setAttachFile(file);
              return false;
            }}
            showUploadList={false}
            maxCount={1}
            accept=".pdf,.docx,.txt"
          >
            <button
              type="button"
              className="px-2 py-2 text-sm bg-[#50B553] text-white rounded hover:bg-[#4AA642]"
            >
              <UploadOutlined />{" "}
              <FormattedMessage
                id="ATTACH_FILES.SELECT_FILE"
                defaultMessage="Chọn tệp"
              />
            </button>
          </Upload>

          {attachFile && (
            <div className="flex items-center gap-2 text-xs text-gray-700 max-w-xs truncate">
              <span className="truncate">{attachFile.name}</span>
              <button
                onClick={() => setAttachFile(null)}
                className="text-red-500 hover:text-red-700 font-semibold"
                type="button"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          )}
        </div>

        <label className="text-[#626262] text-base font-normal">
          <span className="inline-flex items-center gap-1">
            <span className="text-red-500">*</span>
            <FormattedMessage id="ATTACH_FILES.NAME" defaultMessage="Name" />
          </span>
        </label>
        <Tooltip
          title={attachValidationErrors.name}
          open={!!attachValidationErrors.name}
          placement="topLeft"
          color="#ff4d4f"
        >
          <input
            type="text"
            className={`w-full border rounded px-3 py-2 bg-white text-sm hover:border-[#50B553] focus:border-[#50B553] outline-none ${
              attachValidationErrors.name ? "border-red-500" : ""
            }`}
            value={attachName}
            onChange={(e) => {
              setAttachName(e.target.value);
              if (attachValidationErrors.name) {
                setAttachValidationErrors((prev) => {
                  const next = { ...prev };
                  delete next.name;
                  return next;
                });
              }
            }}
            autoFocus
            required
          />
        </Tooltip>

        <label className="text-[#626262] text-base font-normal">
          <span className="inline-flex items-center gap-1">
            <span className="text-red-500">*</span>
            <FormattedMessage id="ATTACH_FILES.DETAIL" defaultMessage="Detail" />
          </span>
        </label>
        <Tooltip
          title={attachValidationErrors.detail}
          open={!!attachValidationErrors.detail}
          placement="topLeft"
          color="#ff4d4f"
        >
          <textarea
            className={`w-full border rounded px-3 py-2 bg-white text-sm hover:border-[#50B553] focus:border-[#50B553] outline-none ${
              attachValidationErrors.detail ? "border-red-500" : ""
            }`}
            value={attachDetail}
            onChange={(e) => {
              setAttachDetail(e.target.value);
              if (attachValidationErrors.detail) {
                setAttachValidationErrors((prev) => {
                  const next = { ...prev };
                  delete next.detail;
                  return next;
                });
              }
            }}
            rows={4}
            required
          />
        </Tooltip>

        <div className="flex justify-end gap-2 mt-2">
          <AntdButton
            onClick={() => {
              setShowAttachModal(false);
              setAttachValidationErrors({});
            }}
            disabled={isPending}
          >
            <FormattedMessage id="ATTACH_FILES.CANCEL" defaultMessage="Cancel" />
          </AntdButton>
          <AntdButton type="primary" htmlType="submit" loading={isPending}>
            <FormattedMessage id="ATTACH_FILES.ATTACH" defaultMessage="Attach" />
          </AntdButton>
        </div>
      </form>
    </Modal>
  );

  const renderMainModal = () => (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      title={intl.formatMessage({
        id: "AGENTS.REFERENCE_FILES.MANAGE_TITLE",
        defaultMessage: "Quản lý tài liệu",
      })}
      width="90vw"
      maxWidth="5xl"
      className="max-h-[calc(100dvh-60px)] my-auto"
    >
      <div className="flex mb-4 gap-2 items-center justify-between">
        <div className="flex items-center gap-4">
          <Input
            placeholder={intl.formatMessage({
              id: "ATTACH_FILES.SEARCH_PLACEHOLDER",
              defaultMessage: "Tìm kiếm...",
            })}
            prefix={
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="text-gray-400"
              />
            }
            allowClear
            className="w-80"
            value={searchDraft.name}
            onChange={handleSearchChange}
            onPressEnter={handleSearch}
          />
          <button
            className="px-4 py-1.5 bg-primary-1 text-white rounded-lg flex items-center gap-2"
            onClick={handleSearch}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} />{" "}
            <FormattedMessage
              id="ATTACH_FILES.SEARCH"
              defaultMessage="Search"
            />
          </button>
          <button
            className="px-4 py-1.5 bg-[#7239EA] text-white rounded-lg flex items-center gap-2 whitespace-nowrap"
            onClick={() => {
              setShowAttachModal(true);
              setAttachValidationErrors({});
            }}
          >
            <KeenIcon icon="paper-clip" />{" "}
            <FormattedMessage
              id="ATTACH_FILES.ATTACH_FILE_BUTTON"
              defaultMessage="Attach file"
            />
          </button>
        </div>

        <div className="items-center">
          <TablePagination
            current={page}
            pageSize={limit}
            total={localAttachData?.total ?? 0}
            onChange={handlePageChange}
            onShowSizeChange={handlePageSizeChange}
            pageSizeOptions={["5", "10", "20", "50", "80", "100"]}
          />
        </div>
      </div>

      <div className="mt-4">
        <Table
          dataSource={localAttachData?.result}
          columns={columns}
          loading={searchLoading}
          rowKey="id"
          pagination={false}
          scroll={{ x: "max-content" }}
          bordered
          size="large"
          locale={{
            emptyText: intl.formatMessage({
              id: "ATTACH_FILES.NO_DATA",
              defaultMessage: "Không có dữ liệu",
            }),
          }}
          className="custom-table"
          {...tableProps}
        />

        <div className="flex justify-end mt-4">
          <TablePagination
            current={page}
            pageSize={limit}
            total={localAttachData?.total ?? 0}
            onChange={handlePageChange}
            onShowSizeChange={handlePageSizeChange}
            pageSizeOptions={["5", "10", "20", "50", "80", "100"]}
          />
        </div>
      </div>
    </Modal>
  );

  // ===== MAIN RENDER =====
  return (
    <>
      <div className="flex items-center justify-between">
        <button
          className={`px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors text-sm mr-2${className}  w-full`}
          onClick={() => setOpen(true)}
        >
          <FormattedMessage id="ATTACH_FILES.MANAGE" defaultMessage="Manage" />
        </button>
        {/* <Checkbox className='text-xs'>Enable Auto Extract</Checkbox> */}
      </div>

      {renderAttachModal()}
      {renderEditModal()}
      {renderMainModal()}
    </>
  );
};

ManageAttach.propTypes = {
  tableProps: PropTypes.object,
  className: PropTypes.string,
};

export { ManageAttach };
